
import { useSession } from "@supabase/auth-helpers-react";
import { Navigation } from "@/components/Navigation";
import { PremiumFeatures } from "@/components/PremiumFeatures";
import { AuthSection } from "@/components/premium/AuthSection";
import { PricingDisplay } from "@/components/premium/PricingDisplay";
import { WelcomeDialog } from "@/components/premium/WelcomeDialog";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import ReactConfetti from 'react-confetti';
import { supabase } from "@/integrations/supabase/client";

export default function Premium() {
  const session = useSession();
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [showWelcomeDialog, setShowWelcomeDialog] = useState(false);
  const [isNewUser, setIsNewUser] = useState(false);

  // Ensure the page scrolls to top when mounted
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Check if user needs onboarding - only for new users, not existing ones
  useEffect(() => {
    const checkUserStatus = async () => {
      if (session?.user) {
        const { data: profile } = await supabase
          .from('profiles')
          .select('onboarded, full_name, created_at')
          .eq('id', session.user.id)
          .single();
        
        // Only show welcome dialog for truly new users (created within last 5 minutes) who haven't been onboarded
        const isVeryNewUser = profile?.created_at && 
          new Date(profile.created_at).getTime() > Date.now() - 5 * 60 * 1000; // 5 minutes
        
        if (isVeryNewUser && (!profile?.onboarded || !profile?.full_name)) {
          setIsNewUser(true);
          setShowWelcomeDialog(true);
        }
      }
    };
    
    checkUserStatus();
  }, [session]);

  // Check if returning from payment by looking for a success parameter
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.get('success')) {
      setShowSuccess(true);
      setShowConfetti(true);
      
      const planType = searchParams.get('plan') || 'premium';
      const planName = planType === 'church' ? 'Church Plan' : 'Premium';
      
      // Show success toast
      toast({
        title: `Welcome to ${planName}! 🎉`,
        description: `Your ${planName.toLowerCase()} features are now active. Enjoy!`,
        duration: 2000,
      });

      // Clear the success parameter from the URL without triggering a refresh
      window.history.replaceState({}, '', '/premium');
      
      // Redirect to community page after a short delay
      setTimeout(() => {
        navigate('/graceful-petitions');
      }, 2000);

      // Hide confetti after animation
      setTimeout(() => {
        setShowConfetti(false);
      }, 2000);
    }
    
    // Add a small delay to prevent flashing content during auth state changes
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, [location, navigate, toast]);

  const handleWelcomeComplete = () => {
    setShowWelcomeDialog(false);
    setIsNewUser(false);
  };

  // Show loading state while checking session
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-app-background transition-colors duration-300">
        <Loader2 className="w-8 h-8 animate-spin text-app-accent" />
      </div>
    );
  }

  // Only show auth section if not loading and no session
  if (!session) {
    return (
      <>
        <AuthSection disableRedirect={showSuccess} freeSignup={true} />
        <Navigation />
      </>
    );
  }

  return (
    <div className="min-h-screen bg-app-background transition-colors duration-300">
      {showConfetti && (
        <ReactConfetti
          width={window.innerWidth}
          height={window.innerHeight}
          recycle={false}
          numberOfPieces={200}
          gravity={0.3}
        />
      )}
      
      <WelcomeDialog 
        isOpen={showWelcomeDialog} 
        onClose={handleWelcomeComplete} 
      />
      
      <div className="min-h-screen pb-16">
        <div className="container py-8">
          <img 
            src="/lovable-uploads/97cec83c-b4fa-48c3-a12a-a678346efa1f.png"
            alt="Grace Awakens Logo"
            className="w-48 mx-auto mb-8 animate-fade-in"
          />
          
          {!isNewUser && <PricingDisplay context="settings" />}

          <div className="mt-12">
            <h2 className="text-2xl font-semibold text-center mb-8 text-app-text-primary transition-colors duration-300">Premium Features</h2>
            <PremiumFeatures />
          </div>
        </div>
        <Navigation />
      </div>
    </div>
  );
}
